import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

export const DOCCHAT_FEEDBACK_COMMENT_MAX_LENGTH = 4000

export const DOCCHAT_FEEDBACK_REASONS = [
  'incorrect_or_incomplete',
  'not_what_i_asked',
  'wrong_documents',
  'slow_or_buggy',
  'style_or_tone',
  'other',
] as const

export type DocChatFeedbackReason = (typeof DOCCHAT_FEEDBACK_REASONS)[number]

export const DOCCHAT_FEEDBACK_REASON_LABELS: Record<
  DocChatFeedbackReason,
  MessageDescriptor
> = {
  incorrect_or_incomplete: msg`Incorrect or incomplete`,
  not_what_i_asked: msg`Not what I asked for`,
  wrong_documents: msg`Wrong or missing documents`,
  slow_or_buggy: msg`Slow or buggy`,
  style_or_tone: msg`Style or tone`,
  other: msg`Other`,
}

export interface DocChatFeedbackRequest {
  conversation_id: string
  turn_id: string
  site_id: string
  rating: 'down'
  reasons: DocChatFeedbackReason[]
  comment: string | null
  selected_text: string | null
  forecast_version: string | null
  url: string | null
}

export interface DocChatFeedbackSaved {
  id: string
  model: string | null
}

export function buildDocChatFeedbackRequest(input: {
  conversationId: string
  turnId: string
  siteId: string
  reasons: DocChatFeedbackReason[]
  comment: string
  selectedText?: string | null
  forecastVersion: string | null
  url: string | null
}): DocChatFeedbackRequest {
  const comment = input.comment.trim()
  const selectedText = input.selectedText?.trim() ?? ''
  return {
    conversation_id: input.conversationId,
    turn_id: input.turnId,
    site_id: input.siteId,
    rating: 'down',
    reasons: [...new Set(input.reasons)],
    comment: comment ? comment : null,
    selected_text: selectedText ? selectedText : null,
    forecast_version: input.forecastVersion,
    url: input.url,
  }
}

export function toDocChatFeedbackReasons(
  values: string[]
): DocChatFeedbackReason[] {
  return DOCCHAT_FEEDBACK_REASONS.filter((reason) => values.includes(reason))
}

export function canSubmitDocChatFeedback(
  reasons: DocChatFeedbackReason[],
  comment: string
): boolean {
  return reasons.length > 0 || comment.trim().length > 0
}
